import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@nexus/ui";
import {
  NumberField,
  SettingRow,
  SettingSwitch,
  SettingsCard,
} from "@/features/changelog/components/shared/SettingsCard";
import { LIMITS, MS_PER_SECOND, SPEND_ALERT_WINDOWS } from "./ops-settings-limits";
import { SlackWarning } from "./SlackWarning";
import type { OpsSettingsCardProps } from "./types";

/**
 * When an expensive or slow stretch is worth a Slack message. These share a
 * window and a quiet period with each other, which is why they sit together
 * rather than under the tab whose numbers they watch.
 */
export function TokenAlertsSettingsCard({
  draft,
  set,
  isReadOnly,
  isSlackConfigured,
}: OpsSettingsCardProps) {
  const { t } = useTranslation();
  const anyAlertOn =
    draft.alertOnTokenSpend ||
    draft.alertOnTenantTokenSpend ||
    draft.alertOnLlmLatency ||
    draft.alertOnLlmFailureRate;

  return (
    <SettingsCard
      icon="flag"
      title={t("admin.opsSettings.tokenAlerts.title", "Token usage alerts")}
      description={t(
        "admin.opsSettings.tokenAlerts.description",
        "When an expensive or slow stretch is worth a Slack message. Each alert is deduped on its own, so one noisy tenant cannot mute the rest.",
      )}
    >
      <SettingRow
        label={t("admin.opsSettings.alertWindow", "Measure over")}
        description={t(
          "admin.opsSettings.alertWindowHelp",
          "The stretch every alert below is totalled across. An hour catches a spike; a day catches a slow leak that never spikes hard enough to trip the hourly figure.",
        )}
        control={
          <Select
            value={String(draft.tokenSpendAlertWindowHours)}
            disabled={isReadOnly}
            onValueChange={(value) => set("tokenSpendAlertWindowHours", Number(value))}
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SPEND_ALERT_WINDOWS.map((hours) => (
                <SelectItem key={hours} value={String(hours)} className="text-xs">
                  {t("admin.opsSettings.lastHours", {
                    defaultValue_one: "The last hour",
                    defaultValue_other: "The last {{count}} hours",
                    count: hours,
                  })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <SettingRow
        htmlFor="token-alert-cooldown"
        label={t("admin.opsSettings.alertCooldown", "Stay quiet for")}
        description={t(
          "admin.opsSettings.alertCooldownHelp",
          "After an alert fires, how long before the same one may fire again. Once a threshold is crossed it stays crossed, so a second message says nothing the first did not.",
        )}
        control={
          <NumberField
            id="token-alert-cooldown"
            value={draft.tokenSpendAlertCooldownHours}
            min={LIMITS.tokenSpendAlertCooldownHours.min}
            max={LIMITS.tokenSpendAlertCooldownHours.max}
            unit={t("admin.opsSettings.hours", "hours")}
            disabled={isReadOnly}
            onChange={(value) => set("tokenSpendAlertCooldownHours", value)}
          />
        }
      />

      <SettingRow
        htmlFor="token-spend-alert"
        label={t("admin.opsSettings.spendAlert", "Post to Slack when spend spikes")}
        description={t(
          "admin.opsSettings.spendAlertHelp",
          "Totalled across every model and tenant. The message breaks the window down by tenant, feature and model, so the first question the channel asks is already answered.",
        )}
        control={
          <SettingSwitch
            id="token-spend-alert"
            checked={draft.alertOnTokenSpend}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("alertOnTokenSpend", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="token-spend-threshold"
        isNested
        isMuted={!draft.alertOnTokenSpend}
        label={t("admin.opsSettings.spendThreshold", "Alert when at least")}
        description={t(
          "admin.opsSettings.spendThresholdHelp",
          "Set it above a normal busy window, or the alert becomes the noise.",
        )}
        control={
          <NumberField
            id="token-spend-threshold"
            value={draft.tokenSpendAlertThreshold}
            min={LIMITS.tokenSpendAlertThreshold.min}
            max={LIMITS.tokenSpendAlertThreshold.max}
            unit={t("admin.opsSettings.tokens", "tokens")}
            disabled={isReadOnly || !draft.alertOnTokenSpend}
            onChange={(value) => set("tokenSpendAlertThreshold", value)}
          />
        }
      />

      <SettingRow
        htmlFor="tenant-spend-alert"
        label={t("admin.opsSettings.tenantSpendAlert", "Post to Slack when one tenant spikes")}
        description={t(
          "admin.opsSettings.tenantSpendAlertHelp",
          "The platform total hides the usual failure: one customer running away while everyone else is normal. Each tenant gets its own message and its own quiet period.",
        )}
        control={
          <SettingSwitch
            id="tenant-spend-alert"
            checked={draft.alertOnTenantTokenSpend}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("alertOnTenantTokenSpend", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="tenant-spend-threshold"
        isNested
        isMuted={!draft.alertOnTenantTokenSpend}
        label={t("admin.opsSettings.tenantSpendThreshold", "Alert when one tenant passes")}
        description={t(
          "admin.opsSettings.tenantSpendThresholdHelp",
          "Set below the platform threshold — a single tenant reaching the platform figure alone is already an incident.",
        )}
        control={
          <NumberField
            id="tenant-spend-threshold"
            value={draft.tenantTokenSpendAlertThreshold}
            min={LIMITS.tenantTokenSpendAlertThreshold.min}
            max={LIMITS.tenantTokenSpendAlertThreshold.max}
            unit={t("admin.opsSettings.tokens", "tokens")}
            disabled={isReadOnly || !draft.alertOnTenantTokenSpend}
            onChange={(value) => set("tenantTokenSpendAlertThreshold", value)}
          />
        }
      />

      <SettingRow
        htmlFor="llm-latency-alert"
        label={t("admin.opsSettings.latencyAlert", "Post to Slack when models get slow")}
        description={t(
          "admin.opsSettings.latencyAlertHelp",
          "Cost is not the only way Vertex goes wrong. The message names the slowest models, which a spend alert cannot.",
        )}
        control={
          <SettingSwitch
            id="llm-latency-alert"
            checked={draft.alertOnLlmLatency}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("alertOnLlmLatency", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="llm-latency-threshold"
        isNested
        isMuted={!draft.alertOnLlmLatency}
        label={t("admin.opsSettings.latencyThreshold", "Alert when p95 passes")}
        description={t(
          "admin.opsSettings.latencyThresholdHelp",
          "The 95th percentile over the window, so a handful of slow calls in a healthy hour does not trip it.",
        )}
        control={
          <NumberField
            id="llm-latency-threshold"
            value={Math.round(draft.llmLatencyAlertP95Ms / MS_PER_SECOND)}
            min={LIMITS.llmLatencyAlertP95Seconds.min}
            max={LIMITS.llmLatencyAlertP95Seconds.max}
            unit={t("admin.opsSettings.seconds", "seconds")}
            disabled={isReadOnly || !draft.alertOnLlmLatency}
            onChange={(value) => set("llmLatencyAlertP95Ms", value * MS_PER_SECOND)}
          />
        }
      />

      <SettingRow
        htmlFor="llm-failure-rate-alert"
        label={t("admin.opsSettings.failureRateAlert", "Post to Slack when calls fail")}
        description={t(
          "admin.opsSettings.failureRateAlertHelp",
          "The share of calls in the window that produced an error, invalid output, or a timeout.",
        )}
        control={
          <SettingSwitch
            id="llm-failure-rate-alert"
            checked={draft.alertOnLlmFailureRate}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("alertOnLlmFailureRate", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="llm-failure-rate-threshold"
        isNested
        isMuted={!draft.alertOnLlmFailureRate}
        label={t("admin.opsSettings.failureRateThreshold", "Alert when Fail % passes")}
        description={t(
          "admin.opsSettings.failureRateThresholdHelp",
          "Failed calls as a percent of all calls. The same number marks the Fail % column.",
        )}
        control={
          <NumberField
            id="llm-failure-rate-threshold"
            value={draft.llmFailureRateAlertPercent}
            min={1}
            max={100}
            unit="%"
            disabled={isReadOnly || !draft.alertOnLlmFailureRate}
            onChange={(value) => set("llmFailureRateAlertPercent", value)}
          />
        }
      />

      {anyAlertOn && !isSlackConfigured ? <SlackWarning /> : null}
    </SettingsCard>
  );
}
